import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Accordion,
  Button,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
  mediaHelper,
} from '@property.com.au/ui-kit';
import { ComparablePropertyAccordionItem } from '../ComparablePropertyAccordionItem/ComparablePropertyAccordionItem';
import type { PropertyAccordionItem } from '../../types';
import {
  DESKTOP_PROPERTY_ADDRESS_WIDTH,
  DESKTOP_PROPERTY_FACT_WIDTH,
} from '../ComparablePropertyAccordionItem/ComparablePropertyAccordionItem.styled';
import type { ShowMoreTilesData } from '../../../../../analytics/event-tracking/trackShowMoreTiles';
import { PropertyCarousel } from '../../../../../app-kit/components/PropertyCarousel/PropertyCarousel';
import type { PropertyTileProps } from '../../../../../app-kit/components/PropertyTile/PropertyTile';

const DescriptionText = styled(Text)`
  ${typographyHelper('body01')}
  color: ${paletteHelper('textSecondary')};
`;

const DesktopPropertiesFactHeaderWrapper = styled.div`
  ${mediaHelper('tabletMax')} {
    display: none;
  }
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  /* stylelint-disable declaration-block-no-redundant-longhand-properties */
  /* border + name size + margin + margin */
  margin-left: calc(
    1px + ${DESKTOP_PROPERTY_ADDRESS_WIDTH}px + ${spacingHelper('medium')} +
      ${spacingHelper('medium')}
  );
  /* border + margin + accordion + margin */
  margin-right: calc(
    1px + ${spacingHelper('medium')} + ${spacingHelper('medium')} + ${spacingHelper('medium')}
  );
  margin-top: ${spacingHelper('medium')};
  margin-bottom: ${spacingHelper('extraSmall')};
  /* stylelint-enable declaration-block-no-redundant-longhand-properties */
`;

const PropertiesFactHeader = styled(Text)<{ $index: number }>`
  ${typographyHelper('overline')};
  color: ${paletteHelper('textSecondary')};
  width: ${(props) => DESKTOP_PROPERTY_FACT_WIDTH[props.$index]}px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: ${spacingHelper('medium')};
`;

const HiddenItemsWrapper = styled.div.attrs<{ $isHidden?: boolean }>(({ $isHidden }) => {
  return $isHidden ? { 'aria-hidden': true } : {};
})`
  ${({ $isHidden }) =>
    $isHidden &&
    css`
      display: none;
    `}
`;

const ShowMoreButton = styled(Button)`
  ${typographyHelper('overline')};
  display: block;
  margin: ${spacingHelper('large auto extraSmall')};
`;

const TitlesWrapper = styled.div`
  margin-bottom: ${spacingHelper('twoExtraSmall')};
`;

const OtherPropertiesTitle = styled(Text)`
  ${typographyHelper('subtitle01')};
  font-size: 1rem;
`;

const Subtitle = styled(Text)`
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textSecondary')};
`;

const OtherSoldProperties = styled.div`
  margin-top: ${spacingHelper('large')};
`;

interface MarketTrendsComparablePropertiesProps {
  description: string;
  factsHeader: string[];
  items: {
    visible: PropertyAccordionItem[];
    hidden?: PropertyAccordionItem[] | null;
  };
  showMoreButton?: { title: string } | null;
  otherSoldProperties?: {
    title: string;
    subtitle?: string | null;
    properties: PropertyTileProps[];
    tracking?: { showMoreProperties?: ShowMoreTilesData | null } | null;
  } | null;
}

export const MarketTrendsComparableProperties: FunctionComponent<
  MarketTrendsComparablePropertiesProps
> = ({ description, factsHeader, items, showMoreButton, otherSoldProperties }) => {
  const [hideHiddenItems, setHideHiddenItems] = useState<boolean>(true);

  return (
    <section>
      <DescriptionText>{description}</DescriptionText>

      <DesktopPropertiesFactHeaderWrapper>
        {factsHeader.map((header, index) => (
          <PropertiesFactHeader key={header} $index={index}>
            {header}
          </PropertiesFactHeader>
        ))}
      </DesktopPropertiesFactHeaderWrapper>

      <StyledAccordion>
        {items.visible.map(({ isDefaultOpen, tracking, property }) => (
          <ComparablePropertyAccordionItem
            key={property.address}
            isDefaultOpen={isDefaultOpen}
            tracking={tracking}
            property={property}
          />
        ))}
        <HiddenItemsWrapper $isHidden={hideHiddenItems}>
          {items.hidden &&
            items.hidden.map(({ isDefaultOpen, tracking, property }) => (
              <ComparablePropertyAccordionItem
                key={property.address}
                isDefaultOpen={isDefaultOpen}
                tracking={tracking}
                property={property}
              />
            ))}
          {otherSoldProperties && (
            <OtherSoldProperties>
              <TitlesWrapper>
                <OtherPropertiesTitle>{otherSoldProperties.title}</OtherPropertiesTitle>
                <Subtitle>{otherSoldProperties.subtitle}</Subtitle>
              </TitlesWrapper>
              <PropertyCarousel
                properties={otherSoldProperties.properties}
                tracking={otherSoldProperties.tracking?.showMoreProperties}
              />
            </OtherSoldProperties>
          )}
        </HiddenItemsWrapper>
      </StyledAccordion>

      {showMoreButton && hideHiddenItems && (
        <ShowMoreButton variant="link-primary" onClick={() => setHideHiddenItems(false)}>
          {showMoreButton.title}
        </ShowMoreButton>
      )}
    </section>
  );
};
